import { type Options } from '@atlassiansox/cross-flow-support';
import { css } from '@compiled/react';
import { ConfluenceBannerMessage } from '@post-office/components--confluence-banner-message';
import { useMessageContext } from '@atlassian/post-office-context/message-context';
import { type ComponentProps, type FunctionComponent, useMemo } from 'react';

import { CTAButton } from './components/cta';
import { Divider } from './components/divider';
import { ValueProposition } from './components/value-proposition';
import { type App } from './types';
import { getCrossflowOptionsForConfluenceHomeBanner } from './util/cross-flow-options';
import { type JSX } from 'react/jsx-runtime';

export type Props = {
	app: App;
	/** Should be used for hiding the message when dismissed. The messageDismissed event should not need to be fired using this. It will be fired internally within the message */
	onDismissed?: (messageInstanceId: string) => void;
	whyAmISeeingThisModalContent?: ComponentProps<
		typeof ConfluenceBannerMessage
	>['whyAmISeeingThisModalContent'];
	extraAnalyticsAttributes?: Record<string, string>;
};

type PropsWithApp = {
	app: App;
};

const bodyContentContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	height: 'fit-content',
	width: '100%',
});

const logoContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	height: '42px',
	marginRight: '16px',
	'> img, > svg': {
		maxHeight: '100% !important',
	},
});

const BodyContent: FunctionComponent<PropsWithApp> = ({ app }) => {
	return (
		<div css={bodyContentContainerStyles}>
			<div css={logoContainerStyles}>{app.logo}</div>
			<Divider />
			<ValueProposition app={app} />
		</div>
	);
};

const CtaContent: FunctionComponent<PropsWithApp> = ({ app }) => <CTAButton app={app} />;

const RECOMMENDATION_MESSAGE_SOURCE_CONTEXT = 'recommendation-message';

export const ConfluenceBannerFirstPartyRecommendation = ({
	app,
	onDismissed,
	whyAmISeeingThisModalContent,
}: Props): JSX.Element => {
	const crossflowOptions: Options = useMemo(
		() =>
			getCrossflowOptionsForConfluenceHomeBanner({
				app,
				sourceContext: RECOMMENDATION_MESSAGE_SOURCE_CONTEXT,
			}),
		[app],
	);

	const { messageInstanceId } = useMessageContext();
	if (!messageInstanceId) {
		throw new Error('messageInstanceId is required');
	}
	return (
		<ConfluenceBannerMessage
			onDismissed={onDismissed}
			whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
			bodyContent={<BodyContent app={app} />}
			ctaContent={<CtaContent app={app} />}
			crossflowOptions={crossflowOptions}
		/>
	);
};
